import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, combineLatest, interval, Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, take, throttleTime } from 'rxjs/operators';
const stageApiUrl = 'https://stage.api.lohi.ai';
const prodApiUrl = 'https://api.lohi.ai';
const featureFlagKey = 'FEATURE_FLAG_API_URL';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private overrides$$ = new BehaviorSubject<Record<string, boolean>>({});
  private flags$$ = new BehaviorSubject<Record<string, boolean>>(null);
  private flagPoll$$ = new Subject<null>();
  private apiUrl$$ = new BehaviorSubject<string>(environment.api);
  public flagEnvironment$: Observable<'Stage' | 'Prod'> = this.apiUrl$$.pipe(
    map((apiUrl) => (apiUrl === prodApiUrl ? 'Prod' : 'Stage')),
  );

  public flags$ = combineLatest([this.flags$$, this.overrides$$]).pipe(
    filter(([flags]) => !!flags),
    map(([flags, overrides]) => Object.assign({}, flags, overrides)),
    shareReplay(1),
  );

  constructor(private httpClient: HttpClient) {
    this.setupWindowFunctions();
    this.loadFlags();
    this.pollFlags();
  }

  public useProdFlags() {
    localStorage.setItem(featureFlagKey, prodApiUrl);
    this.apiUrl$$.next(prodApiUrl);
    this.loadFlags();
  }

  public useStageFlags() {
    localStorage.setItem(featureFlagKey, stageApiUrl);
    this.apiUrl$$.next(stageApiUrl);
    this.loadFlags();
  }

  public isFlagActive$(flag: string): Observable<boolean> {
    return this.flags$.pipe(
      map((flags) => {
        return processFlag(flags, flag);
      }),
    );
  }

  public isFlagActiveNow(flag: string): Promise<boolean> {
    return this.flags$
      .pipe(
        filter((flags) => !!flags),
        map((flags) => {
          return processFlag(flags, flag);
        }),
        take(1),
      )
      .toPromise();
  }

  private pollFlags() {
    this.flagPoll$$.pipe(throttleTime(30 * 1000)).subscribe(() => {
      this.loadFlags();
    });
    interval(5 * 60 * 1000).subscribe(() => {
      if (environment.pollingEnabled) {
        this.flagPoll$$.next(null);
      }
    });
  }

  private async loadFlags() {
    const flags = await this.httpClient
      .get<{ name: string; active: boolean }[]>(`${this.apiUrl$$.value}/v1/feature_flags`)
      .toPromise();
    if (flags) {
      const flagMap: Record<string, boolean> = (flags || []).reduce((acc, flag) => {
        acc[flag.name] = flag.active;
        return acc;
      }, {});
      this.flags$$.next(flagMap);
    } else {
      this.flags$$.next({});
    }
  }

  private setupWindowFunctions() {
    (window as any).listFeatureFlags = () => ({
      production: environment.production,
      flags: this.flags$$.value,
    });

    if (!environment.production) {
      (window as any).setFeatureFlagOverrides = (overrides: Record<string, boolean>) => {
        this.overrides$$.next(overrides || {});
        // Send out same flags so observable pushes out again
        if (Object.keys(overrides).length === 0 && overrides.constructor === Object) {
          console.log('Feature flags are now in sync with firebase');
        } else {
          console.log(
            'Feature flags will be out of sync with firebase until you refresh or call setFeatureFlagOverrides({})',
          );
        }
      };
    }
  }
}

export function processFlag(flags: Record<string, boolean>, flag: string): boolean {
  let targetState = true;
  if (flag.startsWith('!')) {
    flag = flag.substr(1);
    targetState = false;
  }
  if (Reflect.has(flags, flag)) {
    return flags[flag] === targetState;
  }
  return false;
}
